import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import LanguageDropdown from "@me-team/host/src/components/LanguageDropdown/LanguageDropdown";
import { Container, Col, Button, Form, Modal, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import Logo from "@me-team/host/src/images/logo_lg.svg";
import { useReactiveVar } from "@apollo/client";
import { refetchUserDataMenu } from "@me-team/host/src/apollo/apollo-client";
import {
    useGetBranchesForCompanyQuery,
    useUpdateCurrentBranchMutation,
} from "@me-team/host/main/branches/graphql/branches.hooks";
import { currentBranchIdVar, currentCompanyIdVar, currentUserEmailVar } from "@me-team/host/main/globalVar/state";
import { useGetLoginCompaniesQuery } from "@me-team/host/main/company/graphql/company.hooks";

interface ActiveCompaniesProps {
    show: boolean;
    close: () => void;
    isButtonModalClose?: boolean;
}
const ModalUserActiveCompanies: React.FC<ActiveCompaniesProps> = ({ show, close, isButtonModalClose }) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentUserEmail = useReactiveVar(currentUserEmailVar);
    const basePath = process.env.REACT_APP_POS_URL;

    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(+currentCompanyId);
    const [currentEmployeeId, setCurrentEmployeeId] = useState<number>(null);
    const [updateCurrentBranch] = useUpdateCurrentBranchMutation();

    const { data: branchesData } = useGetBranchesForCompanyQuery({
        skip: !selectedCompanyId,
        variables: {
            companyId: selectedCompanyId,
            employeeId: currentEmployeeId,
        },
        context: {
            errorType: "local",
        },
        onError(error: Error) {
            console.error(error);
        },
    });

    const { data: loginCompaniesData } = useGetLoginCompaniesQuery({
        variables: { email: currentUserEmail },
        context: {
            errorType: "global",
        },
    });
    const loginCompanies = loginCompaniesData?.loginCompanies;

    const handleCompanySelect = (companyId: number, employeeId: number) => {
        setSelectedCompanyId(companyId);
        setCurrentEmployeeId(employeeId);
    };

    const branches = branchesData?.user?.company?.branchesPaginated?.branches;
    const firstValidEmployee = branches
        ?.flatMap((branch) => branch.employeesPaginated?.employees || [])
        ?.find((employee) => employee?.userBranchRelations?.length > 0);
    const activeBranchId = firstValidEmployee?.userBranchRelations?.[0]?.branch?.id;

    const handleChangeCompany = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (selectedCompanyId === Number(currentCompanyId)) {
            close();
            return;
        }
        const currentBranchId = activeBranchId || branches[0]?.id;
        try {
            await updateCurrentBranch({
                variables: { companyId: selectedCompanyId, branchId: currentBranchId },
                onCompleted: () => {
                    currentCompanyIdVar(String(selectedCompanyId));
                    currentBranchIdVar(String(currentBranchId));
                },
            });
            if (activeBranchId && !!currentEmployeeId) {
                window.location.href = `/branches/main/branch/${currentBranchId}`;
            } else {
                window.location.href = `/create-owner/${currentBranchId}`;
            }
            await refetchUserDataMenu();
        } catch (error) {
            console.error("Error updating current branch:", error);
        }
        close();
    };

    return (
        <>
            <Modal centered size={"lg"} show={show} onHide={close}>
                {isButtonModalClose && <Modal.Header className="border-0" closeButton></Modal.Header>}
                <Container className="text-dark pt-3">
                    <Col lg={9} className="mx-auto">
                        <div className="bg-white p-4 p-lg-5 text-center rounded-1">
                            <Link to={`${basePath}`} className="d-block mb-5">
                                <Logo />
                            </Link>
                            <h2 className="fs-4 text-dark mb-4">{t("Select a company and account to sign in")}</h2>

                            <Form>
                                <div className="row gap-3">
                                    {loginCompanies?.map((company, index) => (
                                        <Col
                                            key={index}
                                            xs={12}
                                            className={`text-start ${company?.isBlocked ? "text-secondary" : ""}`}
                                        >
                                            <div className="p-3 border rounded-1 d-flex justify-content-between gap-3">
                                                <Form.Check
                                                    type="radio"
                                                    id={`radio-${company?.id}`}
                                                    label={
                                                        <div className="ps-2 w-100 mb-3 mb-sm-0">
                                                            <span className="d-block">
                                                                {t("Company")}: <strong>{company?.name}</strong>
                                                            </span>
                                                            {company?.employee && (
                                                                <>
                                                                    <span className="d-block my-2">
                                                                        {t("User")}:{" "}
                                                                        <strong>
                                                                            {company.employee.name}{" "}
                                                                            {company.employee.surname}
                                                                        </strong>
                                                                    </span>
                                                                    <span className="d-block">
                                                                        {t("Role")}:{" "}
                                                                        <strong>{company.employee.role}</strong>
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                    }
                                                    className={`form-check-active-companies fs-7`}
                                                    checked={selectedCompanyId === Number(company.id)}
                                                    disabled={company?.isBlocked}
                                                    onChange={() =>
                                                        handleCompanySelect(
                                                            Number(company.id),
                                                            Number(company?.employee?.id)
                                                        )
                                                    }
                                                />
                                                {company?.isBlocked && (
                                                    <div className="mt-2 mt-sm-0">
                                                        <Badge
                                                            bg="additional-orange"
                                                            className="d-flex align-items-center fs-7 text-orange rounded-1 py-1 px-2"
                                                        >
                                                            <span className="text-nowrap">{t("Access disabled")}</span>
                                                            <OverlayTrigger
                                                                placement="bottom"
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t(
                                                                            "Access to this company has been disabled. Please contact your administrator to restore access"
                                                                        )}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i className="bi bi-info-circle fs-7 fs-normal ms-2"></i>
                                                            </OverlayTrigger>
                                                        </Badge>
                                                    </div>
                                                )}
                                                {!company?.employee && (
                                                    <div className="mt-2 mt-sm-0">
                                                        <Badge
                                                            bg="additional-orange"
                                                            className="d-flex align-items-center fs-7 text-orange rounded-1 py-1 px-2"
                                                        >
                                                            <span className="text-nowrap">
                                                                {t("Creation incomplete")}
                                                            </span>
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    ))}
                                </div>
                                <div className="row justify-content-center">
                                    <div className="col-12 mb-3 mt-5">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="w-100 fw-bold py-2"
                                            onClick={handleChangeCompany}
                                        >
                                            {t("Sign In")}
                                        </Button>
                                    </div>
                                </div>
                            </Form>

                            <div className="col-2 mt-4 mx-auto">
                                <LanguageDropdown isShowOnlyLanguagesDropdown branchId={branchId} />
                            </div>
                        </div>
                    </Col>
                </Container>
            </Modal>
        </>
    );
};

export default ModalUserActiveCompanies;
